<template>
  <div class="diagnosis-codes-container p-4 h-100">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <h5 class="mb-0">Diagnosis Codes</h5>
      <div>
        <b-button
          class="pl-3 pr-3 pt-1 pb-1 mr-2"
          variant="primary"
          size="sm"
          @click="addDiagnosisCode"
        >
          <i class="fa fa-plus mr-2"></i>New</b-button
        >
        <!-- <b-button
          class="pl-3 pr-3 pt-1 pb-1 "
          variant="primary"
          size="sm"
          @click="exportExcel"
        >
          <i class="fa fa-table mr-2"></i>Export</b-button
        > -->
      </div>
    </div>
    <grid
      v-if="client && eoc"
      class=""
      ref="grid"
      :groupFeature="false"
      :columns="columns"
      :autoHeight="true"
      :data="items"
      :columnLines="false"
      :searchFeature="true"
      :stripeFeature="false"
      :excelExporterFeature="excelExporterFeature"
      @renderrows="hookDropdownButtons"
      @renderrow="hookDropdownButtons"
    />
    <eoc-view-add-edit-diagnosis-codes-modal
      v-if="showIcdCodeModal"
      v-model="showIcdCodeModal"
      :diagnosisCodeId="formId"
      :eocId="eocId"
      :patientId="clientId"
      @onModify="onDiagnosisCodeModify"
    />
  </div>
</template>

<script>
import Grid from "../../third_party/grid/Grid.vue";
import diagnosisCodeServer from "../../server/diagnosisCode";
import { mapState, mapActions, mapGetters } from "vuex";
import zipcelx from "zipcelx";
import eocServer from "../../server/eoc";
import patientServer from "../../server/patient";
import EocViewAddEditDiagnosisCodesModal from "./EocViewAddEditDiagnosisCodesModal.vue";

export default {
  name: "EocViewDiagnosisCodes",
  components: {
    Grid,
    EocViewAddEditDiagnosisCodesModal
  },
  props: ["eocId", "clientId", "showing"],
  data() {
    return {
      list: [],
      formId: null,
      subscribed: false,
      client: null,
      eoc: null,
      currentView: "current",
      viewOptions: [
        {
          value: "current",
          label: "Current EOC",
          icon: ""
        },
        {
          value: "fullHistory",
          label: "Full History",
          icon: ""
        }
      ],
      showIcdCodeModal: false
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    async fetchData() {
      await this.getEoc();
      await this.getClient();
      this.getDiagnosisCodes();
    },
    async getEoc() {
      this.eoc = await eocServer.get(this.eocId);
    },
    async getClient() {
      this.client = await patientServer.get(this.clientId);
    },
    async getDiagnosisCodes() {
      this.list = await diagnosisCodeServer.query(
        this.currentView == "fullHistory",
        this.currentView == "fullHistory" ? this.clientId : this.eocId
      );
    },
    addDiagnosisCode() {
      this.formId = null;
      this.showIcdCodeModal = true;
    },
    exportExcel() {
      this.$refs.grid.exportExcel();
    },
    onDiagnosisCodeModify() {
      this.showIcdCodeModal = false;
      this.getDiagnosisCodes();
    }
  },
  computed: {
    ...mapState("client", {
      statuses: state =>
        state.diagnosisCodeStatuses.map(diagnosisCodeStatus => ({
          value: diagnosisCodeStatus.id,
          text: diagnosisCodeStatus.name,
          data: diagnosisCodeStatus
        }))
    }),
    ...mapGetters("layout", ["getCurrentAdjustedDateTime"]),
    items() {
      return this.list.map(item => {
        let rowHeight = 50;
        const singleLineHeight = 25;
        const codeDescriptionLength = `${item.icdCode.code} - ${item.icdCode.description}`
          .length;
        const maxContentCellLength =
          item.description.length >= codeDescriptionLength
            ? item.description.length
            : codeDescriptionLength;

        if(maxContentCellLength === item.description.length && item.description.length >= 250){
          rowHeight = 200;
        } else {
          const charsDescriptionPerLine =
          maxContentCellLength === codeDescriptionLength ? 25 : 70;
          const maxFactor = Math.max(
            Math.ceil(maxContentCellLength / charsDescriptionPerLine)
          );
          if (maxFactor > 1) rowHeight += singleLineHeight * (maxFactor - 1);
        }
        
        return {
          ...item,
          rowHeight: rowHeight
        };
      });
    },
    columns() {
      let columns = [
        {
          text: "Code",
          type: "template",
          flex: 1,
          editor: false,
          template: data =>
            data.record.data.icdCode
              ? `<div class="text-wrap">${data.record.data.icdCode.code} - ${data.record.data.icdCode.description}</div>`
              : "",
          sortable(a, b) {
            const codeA = a.icdCode.code ?? "";
            const codeB = b.icdCode.code ?? "";
            return codeA.localeCompare(codeB);
          }
        },
        {
          text: "Status",
          field: "status",
          type: "template",
          width: 120,
          editor: false,
          template: ({ record }) => {
            return `<span class="badge-v2 badge-warning px-4 py-2 text-white badge-fixed-size" style="background: ${record.data.status.color}">
                      ${record.data.status.name}
                    </span>`;
          },
          sortable(a, b) {
            const statusA = a.status ? a.status.id : "";
            const statusB = b.status ? b.status.id : "";
            return statusA.localeCompare(statusB);
          }
        },
        {
          text: "Active from",
          type: "template",
          width: 140,
          editor: false,
          template: data =>
            data.record.data.activeFrom
              ? this.formatDateOnly(data.record.data.activeFrom)
              : "",
          sortable(a, b) {
            const dateA = a.activeFrom ?? "";
            const dateB = b.activeFrom ?? "";
            return dateA.localeCompare(dateB);
          }
        },
        {
          text: "Category",
          field: "category",
          type: "template",
          width: 120,
          editor: false,
          template: ({ record }) => record.data.category,
          sortable(a, b) {
            const categoryA = a.category ? a.category.id : "";
            const categoryB = b.category ? b.category.id : "";
            return categoryA.localeCompare(categoryB);
          }
        },
        {
          text: "Resolved by",
          type: "template",
          width: 140,
          editor: false,
          template: data =>
            data.record.data.resolvedBy
              ? this.formatDateOnly(data.record.data.resolvedBy)
              : "",
          sortable(a, b) {
            const dateA = a.resolvedBy ?? "";
            const dateB = b.resolvedBy ?? "";
            return dateA.localeCompare(dateB);
          }
        },

        {
          text: "Comments",
          type: "template",
          width: 450,
          editor: false,
          template: data =>
            `<div class="text-wrap comment-container">
              <div class="comment">
                ${data.record.data.description}
              </div>
            </div>`,
          sortable(a, b) {
            const commentA = a.description ?? "";
            const commentB = b.description ?? "";
            return commentA.localeCompare(commentB);
          }
        }
      ];
      columns.push({
        text: "",
        type: "action",
        field: "action",
        cellCls: "pointer-cell",
        align: "right",
        width: 80,
        actions: [
          {
            tooltip: "More",
            renderer: ({ record }) => {
              return this.createDropdownButton({
                items: [
                  {
                    content: "Edit",
                    callback: async () => {
                      this.formId = record.id;
                      this.showIcdCodeModal = true;
                    }
                  },
                  {
                    content: "Delete",
                    callback: () => {
                      this.$dialog
                        .alert(
                          {
                            title:
                              "Are you sure you want to delete this record?"
                          },
                          {
                            view: "confirm-delete"
                          }
                        )
                        .then(async () => {
                          await diagnosisCodeServer.delete(record.id);
                          this.getDiagnosisCodes();
                          this.showAlertSuccess();
                        });
                    }
                  }
                ]
              });
            }
          }
        ]
      });
      return columns;
    },
    excelExporterFeature() {
      return {
        zipcelx,
        filename: `${this.eoc.mrn}_${this.client.firstName}_${this.client.middleName}_${this.client.lastName}_DiagnosisCodes`,
        exporterConfig: {
          columns: [
            {
              text: "Code",
              field: "icdCode",
              width: 400,
              renderer(data) {
                return data.record.data.icdCode
                  ? `${data.record.data.icdCode.code} - ${data.record.data.icdCode.description}`
                  : "";
              }
            },
            {
              text: "Status",
              field: "status",
              width: 400,
              renderer(data) {
                return data.record.status ? data.record.status.name : "";
              }
            },
            {
              text: "Active from",
              field: "activeFrom",
              width: 400,
              renderer(data) {
                return data.record.data.activeFrom
                  ? this.formatDateOnly(data.record.data.activeFrom)
                  : "";
              }
            },
            {
              text: "Resolved By",
              field: "resolvedBy",
              width: 400,
              renderer(data) {
                return data.record.data.resolvedBy
                  ? this.formatDateOnly(data.record.data.resolvedBy)
                  : "";
              }
            },
            {
              text: "Comments",
              field: "description",
              width: 400,
              renderer(data) {
                return data.record.data.description;
              }
            }
          ]
        }
      };
    }
  },
  watch: {
    currentView: function() {
      this.getDiagnosisCodes();
    },
    showing: async function(newValue) {
      if (newValue) {
        await this.getDiagnosisCodes();
      }
    }
  }
};
</script>

<style>
.diagnosis-codes-container .b-grid-container .b-container {
  max-height: calc(100vh - 340px) !important;
}

.icd-code-picker-hide-options .vs__dropdown-menu {
  display: none;
}
.icd-code-picker-hide-options.vs--open .vs__dropdown-toggle {
  border-bottom-color: #4044e6 !important;
  border-bottom-width: 2px;
}

.comment-container {
  min-height: 100px;
  height: 100%; 
  padding: 20px 20px 20px 0px; 
  display: flex; 
  align-items: center;
}

.comment {
  max-height:100%;
  overflow-y:auto; 
  padding-right: 10px;
}
</style>
